import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";

const TYPE_LABEL = { island_gold: "Island Gold", affiliate: "Affiliate", vendor: "Vendor", partner: "Partner" };
const PRICE_BANDS = [
    { k: "all", label: "Any price", test: () => true },
    { k: "u20", label: "Under $20", test: (p) => p < 20 },
    { k: "20-50", label: "$20–$50", test: (p) => p >= 20 && p <= 50 },
    { k: "o50", label: "Over $50", test: (p) => p > 50 },
];

function Select({ value, onChange, options, testid }) {
    return (
        <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="bg-black/40 border border-white/20 rounded-sm px-3 py-2 text-sm text-white">
            {options.map((o) => <option key={o.k} value={o.k}>{o.label}</option>)}
        </select>
    );
}

function OfferingTile({ o }) {
    const body = (
        <>
            <div className="aspect-[4/5] overflow-hidden rounded-sm bg-black/40">
                {o.image && <img src={o.image} alt={o.name} loading="lazy" className="w-full h-full object-cover" />}
            </div>
            <p className="mt-3 text-[0.6rem] tracking-[0.3em] uppercase text-[#c9a84c]">
                {o.source_type === "island_gold" ? "Made by Island Gold" : `${TYPE_LABEL[o.source_type]} · ${o.seller_name}`}
            </p>
            <h3 className="font-serif text-xl text-white mt-1">{o.name}</h3>
            <p className="text-sm text-white/80 mt-1">${Number(o.price).toFixed(2)}</p>
            {o.external && (
                <p className="text-xs text-white/70 mt-1 inline-flex items-center gap-1">
                    <ExternalLink size={11} /> {o.disclosure}
                </p>
            )}
        </>
    );
    return o.external ? (
        <a href={o.link} target="_blank" rel="sponsored noopener noreferrer" className="block" data-testid={`viewall-item-${o.key}`}>{body}</a>
    ) : (
        <Link to={o.link} className="block" data-testid={`viewall-item-${o.key}`}>{body}</Link>
    );
}

export default function ViewAllOfferings() {
    const [items, setItems] = useState([]);
    const [type, setType] = useState("all");
    const [seller, setSeller] = useState("all");
    const [cat, setCat] = useState("all");
    const [price, setPrice] = useState("all");

    useEffect(() => {
        api.get("/public/offerings").then(({ data }) => setItems(data.offerings || [])).catch(() => setItems([]));
    }, []);

    const opts = (field, labeler, allLabel) => [
        { k: "all", label: allLabel },
        ...[...new Set(items.map((i) => i[field]).filter(Boolean))].map((v) => ({ k: v, label: labeler ? labeler(v) : v })),
    ];
    const sellerLabel = (id) => items.find((i) => i.seller_id === id)?.seller_name || id;
    const band = PRICE_BANDS.find((b) => b.k === price);
    const shown = useMemo(() => items.filter((i) =>
        (type === "all" || i.source_type === type) &&
        (seller === "all" || i.seller_id === seller) &&
        (cat === "all" || i.category === cat) &&
        band.test(Number(i.price))), [items, type, seller, cat, band]);

    if (items.length === 0) return null;

    return (
        <section id="view-all" className="relative max-w-7xl mx-auto px-5 md:px-10 py-16 border-t border-white/10" data-testid="view-all-section">
            <span className="text-[0.6rem] tracking-[0.35em] uppercase text-[#c9a84c]">View all</span>
            <h2 className="font-serif text-3xl md:text-4xl text-white mt-3">Everything for sale</h2>
            <div className="mt-6 flex flex-wrap gap-3" data-testid="view-all-filters">
                <Select value={type} onChange={setType} options={opts("source_type", (v) => TYPE_LABEL[v] || v, "All offering types")} testid="filter-type" />
                <Select value={seller} onChange={setSeller} options={opts("seller_id", sellerLabel, "All sellers")} testid="filter-seller" />
                <Select value={cat} onChange={setCat} options={opts("category", null, "All categories")} testid="filter-category" />
                <Select value={price} onChange={setPrice} options={PRICE_BANDS} testid="filter-price" />
            </div>
            <p className="mt-4 text-sm text-white/70" data-testid="view-all-count">{shown.length} item{shown.length === 1 ? "" : "s"}</p>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {shown.map((o) => <OfferingTile key={o.key} o={o} />)}
            </div>
        </section>
    );
}
